import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Wrench, 
  Plus, 
  Search, 
  Eye, 
  Calendar,
  Building2,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Filter,
  RefreshCw,
  Loader2,
  Upload,
  File,
  Trash2,
  Receipt,
  DollarSign,
  X,
  Edit,
  Save,
  User,
  Phone,
  Mail,
  Briefcase,
  ChevronDown,
  ChevronRight,
  UserCheck,
  Users,
  ShoppingCart
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('fortis_token');
  return { Authorization: `Bearer ${token}` };
};

const PurchasingServices = ({ viewMode = 'purchasing' }) => {
  // viewMode: 'purchasing' (default), 'muhasebe', veya 'board'
  // muhasebe modunda hakediş sadece 3 onay tamamlandığında görünür
  // board modunda Yönetim Kurulu onayı verilebilir
  const isMuhasebeMode = viewMode === 'muhasebe';
  const isBoardMode = viewMode === 'board';
  
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('aktif');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  
  // Görevi Tamamla modal state
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completeLoading, setCompleteLoading] = useState(false);
  const [completeData, setCompleteData] = useState({
    description: '',
    documents: []
  });
  const completeFileInputRef = useRef(null);
  
  // File input refs
  const contractFileInputRef = useRef(null);
  const editContractFileInputRef = useRef(null);
  
  // Cari hesaplar ve şubeler
  const [cariHesaplar, setCariHesaplar] = useState([]);
  const [branches, setBranches] = useState([]);
  
  // Form state (yeni kayıt için)
  const [formData, setFormData] = useState({
    service_name: '',
    description: '',
    cari_hesap_id: '',
    cari_hesap_name: '',
    // Yetkili kişi bilgileri
    yetkili_adi: '',
    yetkili_gorevi: '',
    yetkili_telefon: '',
    yetkili_email: '',
    branch_id: '',
    branch_name: '',
    start_date: '',
    end_date: '',
    total_amount: '',
    currency: 'TL',
    payment_type: 'pesin',
    installment_count: 0,
    installments: [],
    notes: '',
    // Sözleşme belgeleri
    contract_documents: [],
    // Hakedişler
    hakedisler: []
  });
  
  // Edit form state (düzenleme için)
  const [editData, setEditData] = useState(null);
  
  // Expandable rows state
  const [expandedRows, setExpandedRows] = useState({});
  const [approvingHakedis, setApprovingHakedis] = useState(null);

  // Verileri yükle
  const fetchServices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/purchasing/services`, { headers: getAuthHeaders() });
      setServices(response.data || []);
    } catch (err) {
      console.error('Hizmet alımları yüklenirken hata:', err);
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCariHesaplar = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/global/cari-hesaplar`, { headers: getAuthHeaders() });
      setCariHesaplar(response.data || []);
    } catch (err) {
      console.error('Cari hesaplar yüklenirken hata:', err);
    }
  }, []);

  const fetchBranches = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/branches`, { headers: getAuthHeaders() });
      setBranches(response.data || []);
    } catch (err) {
      console.error('Şubeler yüklenirken hata:', err);
    }
  }, []);

  useEffect(() => {
    fetchServices();
    fetchCariHesaplar();
    fetchBranches();
  }, [fetchServices, fetchCariHesaplar, fetchBranches]);

  // Filtreleme
  const filteredServices = services.filter(service => {
    const matchesSearch = 
      (service.service_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (service.cari_hesap_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (service.service_no || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = activeTab === 'tum' || service.status === activeTab;
    
    return matchesSearch && matchesStatus;
  });

  // Toggle expanded row
  const toggleRowExpand = (serviceId) => {
    setExpandedRows(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
  };

  // Hakediş onaylama
  const handleApproveHakedis = async (serviceId, hakedisId, approvalType) => {
    setApprovingHakedis(`${hakedisId}-${approvalType}`);
    try {
      await axios.post(
        `${API}/api/purchasing/services/${serviceId}/hakedis/${hakedisId}/approve`,
        { approval_type: approvalType },
        { headers: getAuthHeaders() }
      );
      const approvalName = approvalType === 'sube_yonetim' ? 'Şube Yönetim' : 'Yönetim Kurulu';
      toast.success(`${approvalName} onayı verildi`);
      fetchServices(); // Listeyi yenile
    } catch (err) {
      console.error('Onay hatası:', err);
      toast.error('Onay verilemedi');
    } finally {
      setApprovingHakedis(null);
    }
  };

  // Hakediş onayını geri alma
  const handleRevokeApproval = async (serviceId, hakedisId, approvalType) => {
    setApprovingHakedis(`${hakedisId}-${approvalType}-revoke`);
    try {
      await axios.post(
        `${API}/api/purchasing/services/${serviceId}/hakedis/${hakedisId}/revoke-approval`,
        { approval_type: approvalType },
        { headers: getAuthHeaders() }
      );
      toast.success('Onay geri alındı');
      fetchServices();
    } catch (err) {
      console.error('Onay geri alma hatası:', err);
      toast.error('Onay geri alınamadı');
    } finally {
      setApprovingHakedis(null);
    }
  };

  // Hakediş'i onaylanan işlere gönder (Muhasebe modunda)
  const handleSendToApprovedJobs = async (serviceId, hakedisId) => {
    setApprovingHakedis(`${hakedisId}-send-approved`);
    try {
      await axios.post(
        `${API}/api/purchasing/services/${serviceId}/hakedis/${hakedisId}/send-to-approved`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Hakediş onaylanan işlere gönderildi');
      fetchServices();
    } catch (err) {
      console.error('Gönderme hatası:', err);
      toast.error(err.response?.data?.detail || 'Gönderme başarısız');
    } finally {
      setApprovingHakedis(null);
    }
  };

  // Form reset
  const resetForm = () => {
    setFormData({
      service_name: '',
      description: '',
      cari_hesap_id: '',
      cari_hesap_name: '',
      yetkili_adi: '',
      yetkili_gorevi: '',
      yetkili_telefon: '',
      yetkili_email: '',
      branch_id: '',
      branch_name: '',
      start_date: '',
      end_date: '',
      total_amount: '',
      currency: 'TL',
      payment_type: 'pesin',
      installment_count: 0,
      installments: [],
      notes: '',
      contract_documents: [],
      hakedisler: []
    });
  };

  // Sözleşme belgesi yükle
  const handleContractFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const formDataUpload = new FormData();
    files.forEach(file => formDataUpload.append('files', file));

    try {
      const response = await axios.post(`${API}/api/upload/offers`, formDataUpload, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setFormData(prev => ({
        ...prev,
        contract_documents: [...prev.contract_documents, ...response.data.files]
      }));
      toast.success('Sözleşme belgesi yüklendi');
    } catch (err) {
      console.error('Dosya yüklenirken hata:', err);
      toast.error('Dosya yüklenemedi');
    }
    
    e.target.value = '';
  };

  // Sözleşme belgesi sil
  const removeContractDocument = (index) => {
    setFormData(prev => ({
      ...prev,
      contract_documents: prev.contract_documents.filter((_, i) => i !== index)
    }));
  };

  // Hakediş ekle
  const addHakedis = () => {
    setFormData(prev => ({
      ...prev,
      hakedisler: [
        ...prev.hakedisler,
        {
          id: Date.now(),
          hakedis_no: prev.hakedisler.length + 1,
          tarih: new Date().toISOString().split('T')[0],
          miktar: '',
          tutar: '',
          aciklama: '',
          belgeler: [],
          durum: 'beklemede'
        }
      ]
    }));
  };

  // Hakediş güncelle
  const updateHakedis = (hakedisId, field, value) => {
    setFormData(prev => ({
      ...prev,
      hakedisler: prev.hakedisler.map(h => 
        h.id === hakedisId ? { ...h, [field]: value } : h
      )
    }));
  };

  // Hakediş sil
  const removeHakedis = (hakedisId) => {
    setFormData(prev => ({
      ...prev,
      hakedisler: prev.hakedisler.filter(h => h.id !== hakedisId)
    }));
  };

  // Hakediş belgesi yükle
  const handleHakedisFileUpload = async (e, hakedisId) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const formDataUpload = new FormData();
    files.forEach(file => formDataUpload.append('files', file));

    try {
      const response = await axios.post(`${API}/api/upload/offers`, formDataUpload, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setFormData(prev => ({
        ...prev,
        hakedisler: prev.hakedisler.map(h => 
          h.id === hakedisId 
            ? { ...h, belgeler: [...h.belgeler, ...response.data.files] }
            : h
        )
      }));
      toast.success('Hakediş belgesi yüklendi');
    } catch (err) {
      console.error('Dosya yüklenirken hata:', err);
      toast.error('Dosya yüklenemedi');
    }
    
    e.target.value = '';
  };

  // Hakediş belgesi sil
  const removeHakedisDocument = (hakedisId, docIndex) => {
    setFormData(prev => ({
      ...prev,
      hakedisler: prev.hakedisler.map(h => 
        h.id === hakedisId 
          ? { ...h, belgeler: h.belgeler.filter((_, i) => i !== docIndex) }
          : h
      )
    }));
  };

  // Hizmet kaydet
  const handleSave = async () => {
    if (!formData.service_name || !formData.cari_hesap_id || !formData.total_amount) {
      toast.error('Lütfen zorunlu alanları doldurun');
      return;
    }

    try {
      await axios.post(`${API}/api/purchasing/services`, formData, { headers: getAuthHeaders() });
      toast.success('Hizmet kaydedildi');
      setShowAddModal(false);
      resetForm();
      fetchServices();
    } catch (err) {
      console.error('Hizmet kaydedilirken hata:', err);
      toast.error('Hizmet kaydedilemedi');
    }
  };

  // Düzenleme modunu başlat
  const startEditing = () => {
    setEditData({
      ...selectedService,
      hakedisler: selectedService.hakedisler || [],
      contract_documents: selectedService.contract_documents || []
    });
    setIsEditing(true);
  };

  // Düzenlemeyi iptal et
  const cancelEditing = () => {
    setEditData(null);
    setIsEditing(false);
  };

  // Edit form güncelleme
  const updateEditData = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  // Edit - Hakediş ekle
  const addEditHakedis = () => {
    setEditData(prev => ({
      ...prev,
      hakedisler: [
        ...prev.hakedisler,
        {
          id: Date.now(),
          hakedis_no: prev.hakedisler.length + 1,
          tarih: new Date().toISOString().split('T')[0],
          miktar: '',
          tutar: '',
          aciklama: '',
          belgeler: [],
          durum: 'beklemede'
        }
      ]
    }));
  };

  // Edit - Hakediş güncelle
  const updateEditHakedis = (hakedisId, field, value) => {
    setEditData(prev => ({
      ...prev,
      hakedisler: prev.hakedisler.map(h => 
        h.id === hakedisId ? { ...h, [field]: value } : h
      )
    }));
  };

  // Edit - Hakediş sil
  const removeEditHakedis = (hakedisId) => {
    setEditData(prev => ({
      ...prev,
      hakedisler: prev.hakedisler.filter(h => h.id !== hakedisId)
    }));
  };

  // Edit - Sözleşme belgesi yükle
  const handleEditContractFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const formDataUpload = new FormData();
    files.forEach(file => formDataUpload.append('files', file));

    try {
      const response = await axios.post(`${API}/api/upload/offers`, formDataUpload, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setEditData(prev => ({
        ...prev,
        contract_documents: [...prev.contract_documents, ...response.data.files]
      }));
      toast.success('Sözleşme belgesi yüklendi');
    } catch (err) {
      console.error('Dosya yüklenirken hata:', err);
      toast.error('Dosya yüklenemedi');
    }
    
    e.target.value = '';
  };

  // Edit - Sözleşme belgesi sil
  const removeEditContractDocument = (index) => {
    setEditData(prev => ({
      ...prev,
      contract_documents: prev.contract_documents.filter((_, i) => i !== index)
    }));
  };

  // Edit - Hakediş belgesi yükle
  const handleEditHakedisFileUpload = async (e, hakedisId) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const formDataUpload = new FormData();
    files.forEach(file => formDataUpload.append('files', file));

    try {
      const response = await axios.post(`${API}/api/upload/offers`, formDataUpload, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setEditData(prev => ({
        ...prev,
        hakedisler: prev.hakedisler.map(h => 
          h.id === hakedisId 
            ? { ...h, belgeler: [...(h.belgeler || []), ...response.data.files] }
            : h
        )
      }));
      toast.success('Hakediş belgesi yüklendi');
    } catch (err) {
      console.error('Dosya yüklenirken hata:', err);
      toast.error('Dosya yüklenemedi');
    }
    
    e.target.value = '';
  };

  // Edit - Hakediş belgesi sil
  const removeEditHakedisDocument = (hakedisId, docIndex) => {
    setEditData(prev => ({
      ...prev,
      hakedisler: prev.hakedisler.map(h => 
        h.id === hakedisId 
          ? { ...h, belgeler: h.belgeler.filter((_, i) => i !== docIndex) }
          : h
      )
    }));
  };

  // Düzenlemeyi kaydet
  const handleSaveEdit = async () => {
    setEditLoading(true);
    try {
      await axios.put(`${API}/api/purchasing/services/${selectedService.id}`, editData, { headers: getAuthHeaders() });
      toast.success('Değişiklikler kaydedildi');
      setIsEditing(false);
      setEditData(null);
      // Listeyi ve seçili hizmeti güncelle
      fetchServices();
      setSelectedService({ ...selectedService, ...editData });
    } catch (err) {
      console.error('Güncelleme hatası:', err);
      toast.error('Değişiklikler kaydedilemedi');
    } finally {
      setEditLoading(false);
    }
  };

  // Görevi Tamamla - Modal aç
  const openCompleteModal = () => {
    setCompleteData({
      description: '',
      documents: []
    });
    setShowCompleteModal(true);
  };

  // Görevi Tamamla - Belge yükleme
  const handleCompleteFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const res = await axios.post(`${API}/api/upload`, formData, {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        });

        if (res.data.url) {
          setCompleteData(prev => ({
            ...prev,
            documents: [...prev.documents, {
              name: file.name,
              url: res.data.url,
              uploaded_at: new Date().toISOString()
            }]
          }));
          toast.success(`${file.name} yüklendi`);
        }
      } catch (err) {
        console.error('Belge yükleme hatası:', err);
        toast.error(`${file.name} yüklenemedi`);
      }
    }
    
    if (completeFileInputRef.current) {
      completeFileInputRef.current.value = '';
    }
  };

  // Görevi Tamamla - Belge kaldır
  const removeCompleteDocument = (index) => {
    setCompleteData(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  // Görevi Tamamla - Kaydet
  const handleCompleteService = async () => {
    if (!completeData.description.trim()) {
      toast.error('Lütfen açıklama girin');
      return;
    }

    setCompleteLoading(true);
    try {
      await axios.put(`${API}/api/purchasing/services/${selectedService.id}/complete`, {
        description: completeData.description,
        documents: completeData.documents
      }, { headers: getAuthHeaders() });
      
      toast.success('Görev başarıyla tamamlandı');
      setShowCompleteModal(false);
      setShowViewModal(false);
      fetchServices();
    } catch (err) {
      console.error('Tamamlama hatası:', err);
      toast.error('Görev tamamlanamadı');
    } finally {
      setCompleteLoading(false);
    }
  };

  // Edit - Toplam hakediş tutarı
  const getEditTotalHakedis = () => {
    if (!editData) return 0;
    return editData.hakedisler.reduce((sum, h) => sum + (parseFloat(h.tutar) || 0), 0);
  };

  // Para birimi sembolleri
  const currencySymbols = { 'TL': '₺', 'USD': '$', 'EUR': '€', 'GBP': '£' };

  // Durum badge renkleri
  const statusColors = {
    aktif: 'bg-green-100 text-green-700',
    tamamlandi: 'bg-blue-100 text-blue-700',
    iptal: 'bg-red-100 text-red-700',
    beklemede: 'bg-yellow-100 text-yellow-700'
  };

  const statusLabels = {
    aktif: 'Aktif',
    tamamlandi: 'Tamamlandı',
    iptal: 'İptal',
    beklemede: 'Beklemede'
  };

  // Toplam hakediş tutarı
  const getTotalHakedis = () => {
    return formData.hakedisler.reduce((sum, h) => sum + (parseFloat(h.tutar) || 0), 0);
  };

  return (
    <div className="space-y-6" data-testid="purchasing-services">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Wrench className="h-6 w-6 text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Alınan Hizmetler</h1>
            <p className="text-sm text-gray-500">Alınan hizmetleri yönetin</p>
          </div>
        </div>
        <Button 
          onClick={() => setShowAddModal(true)}
          className="bg-purple-600 hover:bg-purple-700 gap-2"
          data-testid="add-service-btn"
        >
          <Plus className="h-4 w-4" />
          Hizmet Ekle
        </Button>
      </div>

      {/* Arama ve Filtreler */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Hizmet adı, firma veya numara ile ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="service-search-input"
              />
            </div>
            <Button variant="outline" onClick={fetchServices} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Yenile
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="aktif" className="gap-2">
            <CheckCircle className="h-4 w-4" />
            Aktif
            <Badge variant="secondary" className="ml-1 text-xs">{services.filter(s => s.status === 'aktif').length}</Badge>
            {services.filter(s => s.status === 'aktif' && s.termination_requested).length > 0 && (
              <Badge className="ml-1 text-xs bg-red-600 text-white">{services.filter(s => s.status === 'aktif' && s.termination_requested).length} talep</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="tamamlandi" className="gap-2">
            <Clock className="h-4 w-4" />
            Tamamlanan
            <Badge variant="secondary" className="ml-1 text-xs">{services.filter(s => s.status === 'tamamlandi').length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="iptal" className="gap-2">
            <XCircle className="h-4 w-4" />
            İptal
            <Badge variant="secondary" className="ml-1 text-xs">{services.filter(s => s.status === 'iptal').length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="tum" className="gap-2">
            <Filter className="h-4 w-4" />
            Tümü
            <Badge variant="secondary" className="ml-1 text-xs">{services.length}</Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
                </div>
              ) : filteredServices.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Wrench className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">Hizmet bulunamadı</p>
                  <p className="text-sm">Yeni hizmet eklemek için yukarıdaki butonu kullanın</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8"></TableHead>
                      <TableHead>Hizmet No</TableHead>
                      <TableHead>Hizmet Adı</TableHead>
                      <TableHead>Firma</TableHead>
                      <TableHead>Şube</TableHead>
                      <TableHead>Başlangıç</TableHead>
                      <TableHead>Bitiş</TableHead>
                      <TableHead>Tutar</TableHead>
                      <TableHead>Hakediş</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead className="text-right">İşlem</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredServices.map((service) => (
                      <React.Fragment key={service.id}>
                        {/* Ana satır */}
                        <TableRow 
                          className={`cursor-pointer transition-colors ${
                            service.termination_requested 
                              ? 'bg-red-50 hover:bg-red-100' 
                              : `hover:bg-purple-50 ${expandedRows[service.id] ? 'bg-purple-50' : ''}`
                          }`}
                          onClick={() => toggleRowExpand(service.id)}
                          data-testid={`service-row-${service.id}`}
                        >
                          <TableCell className="w-8">
                            {expandedRows[service.id] ? (
                              <ChevronDown className="h-4 w-4 text-purple-600" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-gray-400" />
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="font-mono">
                              {service.service_no || '-'}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium">{service.service_name}</TableCell>
                          <TableCell>{service.cari_hesap_name || '-'}</TableCell>
                          <TableCell>{service.branch_name || '-'}</TableCell>
                          <TableCell>
                            {service.start_date ? new Date(service.start_date).toLocaleDateString('tr-TR') : '-'}
                          </TableCell>
                          <TableCell>
                            {service.end_date ? new Date(service.end_date).toLocaleDateString('tr-TR') : '-'}
                          </TableCell>
                          <TableCell className="font-semibold text-green-600">
                            {service.total_amount?.toLocaleString('tr-TR')} {currencySymbols[service.currency] || '₺'}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-blue-50 text-blue-700">
                              {service.hakedisler?.length || 0} adet
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              <Badge className={statusColors[service.status] || 'bg-gray-100 text-gray-700'}>
                                {statusLabels[service.status] || service.status}
                              </Badge>
                              {service.termination_requested && (
                                <Badge className="bg-red-600 text-white text-xs">
                                  Sonlandırma Talebi Geldi
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedService(service);
                                setShowViewModal(true);
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                        
                        {/* Expandable içerik - Hakedişler */}
                        {expandedRows[service.id] && (
                          <TableRow>
                            <TableCell colSpan={11} className="bg-gray-50 p-0">
                              <div className="p-4">
                                {/* Muhasebe modunda sadece 3 onaylı hakedişler gösterilir */}
                                {(() => {
                                  const hakedislerToShow = isMuhasebeMode 
                                    ? (service.hakedisler || []).filter(h => 
                                        h.sube_yonetim_onay && h.yonetim_kurulu_onay && h.satin_alma_onay
                                      )
                                    : (service.hakedisler || []);
                                  
                                  return hakedislerToShow.length > 0 ? (
                                  <div className="space-y-3">
                                    <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                                      <Receipt className="h-4 w-4 text-green-600" />
                                      {isMuhasebeMode ? 'Onaylı Hakedişler' : 'Hakedişler'} ({hakedislerToShow.length})
                                      {isMuhasebeMode && service.hakedisler && service.hakedisler.length > hakedislerToShow.length && (
                                        <span className="text-xs text-amber-600 ml-2">
                                          ({service.hakedisler.length - hakedislerToShow.length} adet onay bekliyor)
                                        </span>
                                      )}
                                    </h4>
                                    <div className="grid gap-3">
                                      {hakedislerToShow.map((hakedis, index) => (
                                        <div 
                                          key={hakedis.id || index} 
                                          className="bg-white rounded-lg border p-4 shadow-sm"
                                          data-testid={`hakedis-row-${hakedis.id}`}
                                        >
                                          <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                              <div className="flex items-center gap-3 mb-2">
                                                <span className="font-semibold text-green-700">
                                                  {index + 1}. Hakediş
                                                </span>
                                                <Badge className={
                                                  hakedis.durum === 'odendi' ? 'bg-green-100 text-green-700' :
                                                  hakedis.durum === 'onaylandi' ? 'bg-blue-100 text-blue-700' :
                                                  hakedis.durum === 'iptal' ? 'bg-red-100 text-red-700' :
                                                  'bg-yellow-100 text-yellow-700'
                                                }>
                                                  {hakedis.durum === 'odendi' ? 'Ödendi' :
                                                   hakedis.durum === 'onaylandi' ? 'Onaylandı' :
                                                   hakedis.durum === 'iptal' ? 'İptal' : 'Beklemede'}
                                                </Badge>
                                              </div>
                                              <div className="grid grid-cols-4 gap-4 text-sm">
                                                <div>
                                                  <span className="text-gray-500">Tarih:</span>
                                                  <p className="font-medium">
                                                    {hakedis.tarih ? new Date(hakedis.tarih).toLocaleDateString('tr-TR') : '-'}
                                                  </p>
                                                </div>
                                                <div>
                                                  <span className="text-gray-500">Miktar:</span>
                                                  <p className="font-medium">{hakedis.miktar || '-'}</p>
                                                </div>
                                                <div>
                                                  <span className="text-gray-500">Tutar:</span>
                                                  <p className="font-semibold text-green-600">
                                                    {parseFloat(hakedis.tutar || 0).toLocaleString('tr-TR')} {currencySymbols[service.currency] || '₺'}
                                                  </p>
                                                </div>
                                                <div>
                                                  <span className="text-gray-500">Açıklama:</span>
                                                  <p className="font-medium">{hakedis.aciklama || '-'}</p>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                          
                                          {/* Onay Butonları */}
                                          <div className="mt-4 pt-4 border-t flex items-center gap-6">
                                            {/* Şube Yönetim Onayı (Sadece görüntüleme - Şube panelinden onaylanır) */}
                                            <div className="flex items-center gap-2">
                                              <div className="flex items-center gap-1">
                                                <UserCheck className="h-4 w-4 text-blue-600" />
                                                <span className="text-sm font-medium text-gray-700">Şube Yönetim</span>
                                              </div>
                                              {hakedis.sube_yonetim_onay ? (
                                                <div className="flex items-center gap-1">
                                                  <CheckCircle className="h-5 w-5 text-green-500" />
                                                  <span className="text-xs text-green-600">
                                                    {hakedis.sube_yonetim_onay_yapan && `(${hakedis.sube_yonetim_onay_yapan})`}
                                                  </span>
                                                </div>
                                              ) : (
                                                <span className="text-xs text-gray-400">Bekliyor</span>
                                              )}
                                            </div>
                                            
                                            {/* Yönetim Kurulu Onayı (Board modunda onaylanabilir) */}
                                            <div className="flex items-center gap-2">
                                              <div className="flex items-center gap-1">
                                                <Users className="h-4 w-4 text-purple-600" />
                                                <span className="text-sm font-medium text-gray-700">Yönetim Kurulu</span>
                                              </div>
                                              {hakedis.yonetim_kurulu_onay ? (
                                                <div className="flex items-center gap-1">
                                                  <CheckCircle className="h-5 w-5 text-green-500" />
                                                  <span className="text-xs text-green-600">
                                                    {hakedis.yonetim_kurulu_onay_yapan && `(${hakedis.yonetim_kurulu_onay_yapan})`}
                                                  </span>
                                                  {isBoardMode && (
                                                    <Button
                                                      variant="ghost"
                                                      size="sm"
                                                      className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRevokeApproval(service.id, hakedis.id, 'yonetim_kurulu');
                                                      }}
                                                      disabled={approvingHakedis === `${hakedis.id}-yonetim_kurulu-revoke`}
                                                    >
                                                      {approvingHakedis === `${hakedis.id}-yonetim_kurulu-revoke` ? (
                                                        <Loader2 className="h-3 w-3 animate-spin" />
                                                      ) : (
                                                        <X className="h-3 w-3" />
                                                      )}
                                                    </Button>
                                                  )}
                                                </div>
                                              ) : isBoardMode ? (
                                                <Button
                                                  variant="outline"
                                                  size="sm"
                                                  className="h-7 gap-1 border-purple-300 text-purple-700 hover:bg-purple-50"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleApproveHakedis(service.id, hakedis.id, 'yonetim_kurulu');
                                                  }}
                                                  disabled={approvingHakedis === `${hakedis.id}-yonetim_kurulu`}
                                                  data-testid={`approve-yonetim-kurulu-btn-${hakedis.id}`}
                                                >
                                                  {approvingHakedis === `${hakedis.id}-yonetim_kurulu` ? (
                                                    <Loader2 className="h-3 w-3 animate-spin" />
                                                  ) : (
                                                    <Plus className="h-3 w-3" />
                                                  )}
                                                  Onayla
                                                </Button>
                                              ) : (
                                                <span className="text-xs text-gray-400">Bekliyor</span>
                                              )}
                                            </div>
                                            
                                            {/* Satın Alma Onayı (Bu panelden onaylanır) */}
                                            <div className="flex items-center gap-2">
                                              <div className="flex items-center gap-1">
                                                <ShoppingCart className="h-4 w-4 text-green-600" />
                                                <span className="text-sm font-medium text-gray-700">Satın Alma</span>
                                              </div>
                                              {hakedis.satin_alma_onay ? (
                                                <div className="flex items-center gap-1">
                                                  <CheckCircle className="h-5 w-5 text-green-500" />
                                                  <span className="text-xs text-green-600">
                                                    {hakedis.satin_alma_onay_yapan && `(${hakedis.satin_alma_onay_yapan})`}
                                                  </span>
                                                  <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleRevokeApproval(service.id, hakedis.id, 'satin_alma');
                                                    }}
                                                    disabled={approvingHakedis === `${hakedis.id}-satin_alma-revoke`}
                                                  >
                                                    {approvingHakedis === `${hakedis.id}-satin_alma-revoke` ? (
                                                      <Loader2 className="h-3 w-3 animate-spin" />
                                                    ) : (
                                                      <X className="h-3 w-3" />
                                                    )}
                                                  </Button>
                                                </div>
                                              ) : (
                                                <Button
                                                  variant="outline"
                                                  size="sm"
                                                  className="h-7 gap-1 border-green-300 text-green-700 hover:bg-green-50"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleApproveHakedis(service.id, hakedis.id, 'satin_alma');
                                                  }}
                                                  disabled={approvingHakedis === `${hakedis.id}-satin_alma`}
                                                  data-testid={`approve-satinalma-btn-${hakedis.id}`}
                                                >
                                                  {approvingHakedis === `${hakedis.id}-satin_alma` ? (
                                                    <Loader2 className="h-3 w-3 animate-spin" />
                                                  ) : (
                                                    <Plus className="h-3 w-3" />
                                                  )}
                                                  Onayla
                                                </Button>
                                              )}
                                            </div>
                                            
                                            {/* Tam Onay Durumu - 3 onay gerekli */}
                                            {hakedis.sube_yonetim_onay && hakedis.yonetim_kurulu_onay && hakedis.satin_alma_onay && (
                                              <>
                                                <Badge className="ml-auto bg-green-100 text-green-700">
                                                  <CheckCircle className="h-3 w-3 mr-1" />
                                                  Tam Onaylı
                                                </Badge>
                                                
                                                {/* Muhasebe modunda "Onaylanan İşlere Gönder" butonu */}
                                                {isMuhasebeMode && (
                                                  hakedis.muhasebe_onaylandi ? (
                                                    <Badge className="bg-blue-100 text-blue-700">
                                                      <CheckCircle className="h-3 w-3 mr-1" />
                                                      İşlere Gönderildi
                                                      {hakedis.muhasebe_onay_yapan && (
                                                        <span className="ml-1 text-xs">({hakedis.muhasebe_onay_yapan})</span>
                                                      )}
                                                    </Badge>
                                                  ) : (
                                                    <Button
                                                      variant="outline"
                                                      size="sm"
                                                      className="h-7 gap-1 border-blue-300 text-blue-700 hover:bg-blue-50"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSendToApprovedJobs(service.id, hakedis.id);
                                                      }}
                                                      disabled={approvingHakedis === `${hakedis.id}-send-approved`}
                                                      data-testid={`send-to-approved-btn-${hakedis.id}`}
                                                    >
                                                      {approvingHakedis === `${hakedis.id}-send-approved` ? (
                                                        <Loader2 className="h-3 w-3 animate-spin" />
                                                      ) : (
                                                        <CheckCircle className="h-3 w-3" />
                                                      )}
                                                      Onaylanan İşlere Gönder
                                                    </Button>
                                                  )
                                                )}
                                              </>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-center py-6 text-gray-500">
                                    <Receipt className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                                    <p>{isMuhasebeMode ? 'Onaylı hakediş bulunmuyor (3 onay gerekli)' : 'Bu hizmet için henüz hakediş girilmemiş'}</p>
                                  </div>
                                );
                                })()}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Hizmet Ekleme Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5 text-purple-600" />
              Yeni Hizmet
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Temel Bilgiler */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-700 border-b pb-2">Temel Bilgiler</h3>
              
              {/* Hizmet Adı */}
              <div className="space-y-2">
                <Label>Hizmet Adı *</Label>
                <Input
                  value={formData.service_name}
                  onChange={(e) => setFormData({ ...formData, service_name: e.target.value })}
                  placeholder="Örn: Temizlik Hizmeti, Güvenlik Hizmeti..."
                />
              </div>

              {/* Açıklama */}
              <div className="space-y-2">
                <Label>Açıklama</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Hizmet detayları..."
                  rows={2}
                />
              </div>

              {/* Cari Hesap ve Şube */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Firma (Cari Hesap) *</Label>
                  <Select
                    value={formData.cari_hesap_id}
                    onValueChange={(value) => {
                      const cari = cariHesaplar.find(c => c.id === value);
                      setFormData({
                        ...formData,
                        cari_hesap_id: value,
                        cari_hesap_name: cari?.name || ''
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Firma seçin..." />
                    </SelectTrigger>
                    <SelectContent>
                      {cariHesaplar.map((cari) => (
                        <SelectItem key={cari.id} value={cari.id}>
                          {cari.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Şube</Label>
                  <Select
                    value={formData.branch_id}
                    onValueChange={(value) => {
                      const branch = branches.find(b => b.id === value);
                      setFormData({
                        ...formData,
                        branch_id: value,
                        branch_name: branch?.name || ''
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Şube seçin..." />
                    </SelectTrigger>
                    <SelectContent>
                      {branches.map((branch) => (
                        <SelectItem key={branch.id} value={branch.id}>
                          {branch.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Yetkili Kişi Bilgileri */}
              <div className="border rounded-lg p-4 bg-blue-50/50">
                <Label className="flex items-center gap-2 mb-3 text-blue-700 font-medium">
                  <User className="h-4 w-4" />
                  Yetkili Kişi Bilgileri
                </Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm flex items-center gap-1">
                      <User className="h-3 w-3" />
                      Yetkili Adı
                    </Label>
                    <Input
                      placeholder="Yetkili kişinin adı soyadı"
                      value={formData.yetkili_adi}
                      onChange={(e) => setFormData({ ...formData, yetkili_adi: e.target.value })}
                      data-testid="yetkili-adi-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm flex items-center gap-1">
                      <Briefcase className="h-3 w-3" />
                      Görevi
                    </Label>
                    <Input
                      placeholder="Görevi / Unvanı"
                      value={formData.yetkili_gorevi}
                      onChange={(e) => setFormData({ ...formData, yetkili_gorevi: e.target.value })}
                      data-testid="yetkili-gorevi-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      Telefon
                    </Label>
                    <Input
                      placeholder="İletişim telefonu"
                      value={formData.yetkili_telefon}
                      onChange={(e) => setFormData({ ...formData, yetkili_telefon: e.target.value })}
                      data-testid="yetkili-telefon-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      E-posta
                    </Label>
                    <Input
                      type="email"
                      placeholder="E-posta adresi"
                      value={formData.yetkili_email}
                      onChange={(e) => setFormData({ ...formData, yetkili_email: e.target.value })}
                      data-testid="yetkili-email-input"
                    />
                  </div>
                </div>
              </div>

              {/* Tarihler */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Başlangıç Tarihi</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Bitiş Tarihi</Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  />
                </div>
              </div>

              {/* Tutar ve Para Birimi */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Toplam Tutar *</Label>
                  <Input
                    type="number"
                    value={formData.total_amount}
                    onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Para Birimi</Label>
                  <Select
                    value={formData.currency}
                    onValueChange={(value) => setFormData({ ...formData, currency: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TL">₺ TL</SelectItem>
                      <SelectItem value="USD">$ USD</SelectItem>
                      <SelectItem value="EUR">€ EUR</SelectItem>
                      <SelectItem value="GBP">£ GBP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Ödeme Tipi</Label>
                  <Select
                    value={formData.payment_type}
                    onValueChange={(value) => setFormData({ ...formData, payment_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pesin">Peşin</SelectItem>
                      <SelectItem value="vadeli">Vadeli</SelectItem>
                      <SelectItem value="aylik">Aylık</SelectItem>
                      <SelectItem value="hakedis">Hakediş Bazlı</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Notlar */}
              <div className="space-y-2">
                <Label>Notlar</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Ek notlar..."
                  rows={2}
                />
              </div>
            </div>

            {/* Sözleşme Belgeleri */}
            <div className="space-y-4 border-t pt-4">
              <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                Sözleşme Belgeleri
              </h3>
              
              <div className="border-2 border-dashed rounded-lg p-4">
                <input
                  type="file"
                  ref={contractFileInputRef}
                  multiple
                  onChange={handleContractFileUpload}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                />
                <div className="flex items-center justify-center gap-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="gap-2 border-blue-300 text-blue-700 hover:bg-blue-50"
                    onClick={() => contractFileInputRef.current?.click()}
                  >
                    <Upload className="h-4 w-4" />
                    Belge Yükle
                  </Button>
                </div>
                
                {formData.contract_documents.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {formData.contract_documents.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between bg-blue-50 rounded-lg p-3">
                        <div className="flex items-center gap-2">
                          <File className="h-4 w-4 text-blue-600" />
                          <span className="text-sm font-medium">{doc.filename}</span>
                          <span className="text-xs text-gray-500">
                            ({(doc.file_size / 1024).toFixed(1)} KB)
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeContractDocument(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                
                {formData.contract_documents.length === 0 && (
                  <p className="text-center text-gray-400 text-sm mt-2">
                    Sözleşme, şartname veya diğer belgeleri yükleyin
                  </p>
                )}
              </div>
            </div>

            {/* Hakedişler */}
            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                  <Receipt className="h-5 w-5 text-green-600" />
                  Hakedişler
                  {formData.hakedisler.length > 0 && (
                    <Badge className="bg-green-100 text-green-700 ml-2">
                      {formData.hakedisler.length} adet
                    </Badge>
                  )}
                </h3>
                <Button
                  type="button"
                  variant="outline"
                  onClick={addHakedis}
                  className="gap-2 border-green-300 text-green-700 hover:bg-green-50"
                >
                  <Plus className="h-4 w-4" />
                  Hakediş Ekle
                </Button>
              </div>

              {formData.hakedisler.length === 0 ? (
                <div className="text-center py-8 text-gray-500 border-2 border-dashed rounded-lg">
                  <Receipt className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>Henüz hakediş eklenmedi</p>
                  <p className="text-sm">"Hakediş Ekle" butonuna tıklayarak hakediş ekleyin</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.hakedisler.map((hakedis, index) => (
                    <div key={hakedis.id} className="border rounded-lg p-4 bg-green-50">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold text-green-700 flex items-center gap-2">
                          <Receipt className="h-4 w-4" />
                          {index + 1}. Hakediş
                        </h4>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeHakedis(hakedis.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm">Tarih</Label>
                          <Input
                            type="date"
                            value={hakedis.tarih}
                            onChange={(e) => updateHakedis(hakedis.id, 'tarih', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">Miktar / İş Yüzdesi</Label>
                          <Input
                            value={hakedis.miktar}
                            onChange={(e) => updateHakedis(hakedis.id, 'miktar', e.target.value)}
                            placeholder="Örn: %25, 100 m², 50 adet"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">Tutar ({currencySymbols[formData.currency]})</Label>
                          <Input
                            type="number"
                            value={hakedis.tutar}
                            onChange={(e) => updateHakedis(hakedis.id, 'tutar', e.target.value)}
                            placeholder="0"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">Durum</Label>
                          <Select
                            value={hakedis.durum}
                            onValueChange={(value) => updateHakedis(hakedis.id, 'durum', value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="beklemede">Beklemede</SelectItem>
                              <SelectItem value="onaylandi">Onaylandı</SelectItem>
                              <SelectItem value="odendi">Ödendi</SelectItem>
                              <SelectItem value="iptal">İptal</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="mt-3 space-y-2">
                        <Label className="text-sm">Açıklama</Label>
                        <Input
                          value={hakedis.aciklama}
                          onChange={(e) => updateHakedis(hakedis.id, 'aciklama', e.target.value)}
                          placeholder="Hakediş açıklaması..."
                        />
                      </div>

                      {/* Hakediş Belgeleri */}
                      <div className="mt-3">
                        <Label className="text-sm mb-2 block">Hakediş Belgeleri</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="file"
                            multiple
                            onChange={(e) => handleHakedisFileUpload(e, hakedis.id)}
                            className="flex-1 text-sm"
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                          />
                        </div>
                        
                        {hakedis.belgeler.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {hakedis.belgeler.map((doc, docIndex) => (
                              <div key={docIndex} className="flex items-center gap-1 bg-white rounded px-2 py-1 text-sm border">
                                <File className="h-3 w-3 text-green-600" />
                                <span className="max-w-[150px] truncate">{doc.filename}</span>
                                <button
                                  type="button"
                                  onClick={() => removeHakedisDocument(hakedis.id, docIndex)}
                                  className="text-red-500 hover:text-red-700 ml-1"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Hakediş Özeti */}
                  {formData.hakedisler.length > 0 && (
                    <div className="bg-green-100 rounded-lg p-4 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-5 w-5 text-green-700" />
                        <span className="font-medium text-green-800">Toplam Hakediş Tutarı:</span>
                      </div>
                      <span className="text-xl font-bold text-green-700">
                        {getTotalHakedis().toLocaleString('tr-TR')} {currencySymbols[formData.currency]}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddModal(false); resetForm(); }}>
              İptal
            </Button>
            <Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700">
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hizmet Detay Modal */}
      <Dialog open={showViewModal} onOpenChange={(open) => { 
        setShowViewModal(open); 
        if (!open) { 
          setIsEditing(false); 
          setEditData(null); 
        } 
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Wrench className="h-5 w-5 text-purple-600" />
                Hizmet Detayı
                {isEditing && <Badge className="bg-orange-100 text-orange-700 ml-2">Düzenleniyor</Badge>}
              </span>
              {!isEditing && selectedService && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={startEditing}
                  className="gap-2"
                >
                  <Edit className="h-4 w-4" />
                  Düzenle
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedService && !isEditing && (
            <div className="space-y-6 py-4">
              {/* Temel Bilgiler - Görüntüleme Modu */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Hizmet No</Label>
                  <p className="font-mono font-semibold">{selectedService.service_no || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Durum</Label>
                  <Badge className={statusColors[selectedService.status] || 'bg-gray-100'}>
                    {statusLabels[selectedService.status] || selectedService.status}
                  </Badge>
                </div>
              </div>

              <div>
                <Label className="text-gray-500">Hizmet Adı</Label>
                <p className="font-semibold">{selectedService.service_name}</p>
              </div>

              {selectedService.description && (
                <div>
                  <Label className="text-gray-500">Açıklama</Label>
                  <p>{selectedService.description}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Firma</Label>
                  <p className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-gray-400" />
                    {selectedService.cari_hesap_name || '-'}
                  </p>
                </div>
                <div>
                  <Label className="text-gray-500">Şube</Label>
                  <p>{selectedService.branch_name || '-'}</p>
                </div>
              </div>

              {/* Yetkili Kişi Bilgileri - Görüntüleme */}
              {(selectedService.yetkili_adi || selectedService.yetkili_gorevi || selectedService.yetkili_telefon || selectedService.yetkili_email) && (
                <div className="border rounded-lg p-4 bg-blue-50/50">
                  <Label className="flex items-center gap-2 mb-3 text-blue-700 font-medium">
                    <User className="h-4 w-4" />
                    Yetkili Kişi Bilgileri
                  </Label>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedService.yetkili_adi && (
                      <div>
                        <Label className="text-gray-500 text-sm">Yetkili Adı</Label>
                        <p className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-400" />
                          {selectedService.yetkili_adi}
                        </p>
                      </div>
                    )}
                    {selectedService.yetkili_gorevi && (
                      <div>
                        <Label className="text-gray-500 text-sm">Görevi</Label>
                        <p className="flex items-center gap-2">
                          <Briefcase className="h-4 w-4 text-gray-400" />
                          {selectedService.yetkili_gorevi}
                        </p>
                      </div>
                    )}
                    {selectedService.yetkili_telefon && (
                      <div>
                        <Label className="text-gray-500 text-sm">Telefon</Label>
                        <p className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-gray-400" />
                          {selectedService.yetkili_telefon}
                        </p>
                      </div>
                    )}
                    {selectedService.yetkili_email && (
                      <div>
                        <Label className="text-gray-500 text-sm">E-posta</Label>
                        <p className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-gray-400" />
                          {selectedService.yetkili_email}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Başlangıç Tarihi</Label>
                  <p className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    {selectedService.start_date ? new Date(selectedService.start_date).toLocaleDateString('tr-TR') : '-'}
                  </p>
                </div>
                <div>
                  <Label className="text-gray-500">Bitiş Tarihi</Label>
                  <p className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    {selectedService.end_date ? new Date(selectedService.end_date).toLocaleDateString('tr-TR') : '-'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Toplam Tutar</Label>
                  <p className="text-xl font-bold text-green-600">
                    {selectedService.total_amount?.toLocaleString('tr-TR')} {currencySymbols[selectedService.currency] || '₺'}
                  </p>
                </div>
                <div>
                  <Label className="text-gray-500">Ödeme Tipi</Label>
                  <p className="capitalize">{selectedService.payment_type || 'Peşin'}</p>
                </div>
              </div>

              {/* Sözleşme Belgeleri - Görüntüleme */}
              <div className="border-t pt-4">
                <Label className="text-gray-500 flex items-center gap-2 mb-2">
                  <FileText className="h-4 w-4" />
                  Sözleşme Belgeleri ({selectedService.contract_documents?.length || 0})
                </Label>
                {selectedService.contract_documents?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {selectedService.contract_documents.map((doc, index) => (
                      <a
                        key={index}
                        href={`${API}${doc.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-blue-50 rounded-lg px-3 py-2 text-sm text-blue-700 hover:bg-blue-100"
                      >
                        <File className="h-4 w-4" />
                        {doc.filename}
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm">Belge yok</p>
                )}
              </div>

              {/* Tamamlama Bilgileri - Sadece tamamlanmış hizmetlerde göster */}
              {selectedService.status === 'tamamlandi' && selectedService.completion_record && (
                <div className="border-t pt-4">
                  <Label className="text-gray-500 flex items-center gap-2 mb-3">
                    <CheckCircle className="h-4 w-4 text-blue-600" />
                    Tamamlama Bilgileri
                  </Label>
                  <div className="bg-blue-50 rounded-lg p-4 space-y-3">
                    <div>
                      <span className="text-sm text-gray-500">Tamamlayan:</span>
                      <p className="font-medium">{selectedService.completion_record.completed_by || '-'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Tamamlanma Tarihi:</span>
                      <p className="font-medium">
                        {selectedService.completion_record.completed_at 
                          ? new Date(selectedService.completion_record.completed_at).toLocaleString('tr-TR')
                          : '-'}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Açıklama:</span>
                      <p className="mt-1 bg-white rounded p-2 text-gray-700">
                        {selectedService.completion_record.description || '-'}
                      </p>
                    </div>
                    {selectedService.completion_record.documents?.length > 0 && (
                      <div>
                        <span className="text-sm text-gray-500">Tamamlama Belgeleri:</span>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {selectedService.completion_record.documents.map((doc, index) => (
                            <a
                              key={index}
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 text-sm text-blue-700 hover:bg-blue-100 border border-blue-200"
                            >
                              <File className="h-4 w-4" />
                              {doc.name}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Hakedişler - Görüntüleme */}
              <div className="border-t pt-4">
                <Label className="text-gray-500 flex items-center gap-2 mb-3">
                  <Receipt className="h-4 w-4" />
                  Hakedişler ({selectedService.hakedisler?.length || 0})
                </Label>
                {selectedService.hakedisler?.length > 0 ? (
                  <div className="space-y-3">
                    {selectedService.hakedisler.map((hakedis, index) => (
                      <div key={index} className="bg-green-50 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-green-700">{index + 1}. Hakediş</span>
                          <Badge className={
                            hakedis.durum === 'odendi' ? 'bg-green-100 text-green-700' :
                            hakedis.durum === 'onaylandi' ? 'bg-blue-100 text-blue-700' :
                            hakedis.durum === 'iptal' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          }>
                            {hakedis.durum === 'odendi' ? 'Ödendi' :
                             hakedis.durum === 'onaylandi' ? 'Onaylandı' :
                             hakedis.durum === 'iptal' ? 'İptal' : 'Beklemede'}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">Tarih:</span>
                            <p>{hakedis.tarih ? new Date(hakedis.tarih).toLocaleDateString('tr-TR') : '-'}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Miktar:</span>
                            <p>{hakedis.miktar || '-'}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Tutar:</span>
                            <p className="font-semibold text-green-600">
                              {parseFloat(hakedis.tutar || 0).toLocaleString('tr-TR')} {currencySymbols[selectedService.currency] || '₺'}
                            </p>
                          </div>
                        </div>
                        {hakedis.aciklama && (
                          <p className="text-sm text-gray-600 mt-2">{hakedis.aciklama}</p>
                        )}
                        {hakedis.belgeler?.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {hakedis.belgeler.map((doc, docIndex) => (
                              <a
                                key={docIndex}
                                href={`${API}${doc.url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 bg-white rounded px-2 py-1 text-xs text-green-700 hover:bg-green-100"
                              >
                                <File className="h-3 w-3" />
                                {doc.filename}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm">Hakediş yok</p>
                )}
              </div>

              {selectedService.notes && (
                <div className="border-t pt-4">
                  <Label className="text-gray-500">Notlar</Label>
                  <p className="text-sm text-gray-600">{selectedService.notes}</p>
                </div>
              )}
            </div>
          )}

          {/* Düzenleme Modu */}
          {isEditing && editData && (
            <div className="space-y-6 py-4">
              {/* Sözleşme Belgeleri - Düzenleme */}
              <div className="space-y-4 border rounded-lg p-4 bg-blue-50">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-blue-600" />
                    Sözleşme Belgeleri
                    {editData.contract_documents?.length > 0 && (
                      <Badge className="bg-blue-100 text-blue-700">
                        {editData.contract_documents.length}
                      </Badge>
                    )}
                  </h3>
                  <div>
                    <input
                      type="file"
                      ref={editContractFileInputRef}
                      multiple
                      onChange={handleEditContractFileUpload}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      className="gap-2 border-blue-300 text-blue-700 hover:bg-blue-100"
                      onClick={() => editContractFileInputRef.current?.click()}
                    >
                      <Upload className="h-4 w-4" />
                      Belge Ekle
                    </Button>
                  </div>
                </div>
                
                {editData.contract_documents?.length > 0 ? (
                  <div className="space-y-2">
                    {editData.contract_documents.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between bg-white rounded-lg p-3">
                        <div className="flex items-center gap-2">
                          <File className="h-4 w-4 text-blue-600" />
                          <span className="text-sm font-medium">{doc.filename}</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeEditContractDocument(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-400 text-sm">Belge yok - Eklemek için yukarıdaki butonu kullanın</p>
                )}
              </div>

              {/* Hakedişler - Düzenleme */}
              <div className="space-y-4 border rounded-lg p-4 bg-green-50">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                    <Receipt className="h-5 w-5 text-green-600" />
                    Hakedişler
                    {editData.hakedisler?.length > 0 && (
                      <Badge className="bg-green-100 text-green-700">
                        {editData.hakedisler.length} adet
                      </Badge>
                    )}
                  </h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addEditHakedis}
                    className="gap-2 border-green-300 text-green-700 hover:bg-green-100"
                  >
                    <Plus className="h-4 w-4" />
                    Hakediş Ekle
                  </Button>
                </div>

                {editData.hakedisler?.length === 0 ? (
                  <div className="text-center py-6 text-gray-500">
                    <Receipt className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                    <p>Henüz hakediş yok</p>
                    <p className="text-sm">"Hakediş Ekle" butonuna tıklayın</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {editData.hakedisler.map((hakedis, index) => (
                      <div key={hakedis.id} className="border rounded-lg p-4 bg-white">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold text-green-700 flex items-center gap-2">
                            <Receipt className="h-4 w-4" />
                            {index + 1}. Hakediş
                          </h4>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeEditHakedis(hakedis.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Tarih</Label>
                            <Input
                              type="date"
                              value={hakedis.tarih || ''}
                              onChange={(e) => updateEditHakedis(hakedis.id, 'tarih', e.target.value)}
                              className="h-9"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Miktar / İş %</Label>
                            <Input
                              value={hakedis.miktar || ''}
                              onChange={(e) => updateEditHakedis(hakedis.id, 'miktar', e.target.value)}
                              placeholder="%25, 100 m²"
                              className="h-9"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Tutar ({currencySymbols[selectedService?.currency] || '₺'})</Label>
                            <Input
                              type="number"
                              value={hakedis.tutar || ''}
                              onChange={(e) => updateEditHakedis(hakedis.id, 'tutar', e.target.value)}
                              placeholder="0"
                              className="h-9"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Durum</Label>
                            <Select
                              value={hakedis.durum || 'beklemede'}
                              onValueChange={(value) => updateEditHakedis(hakedis.id, 'durum', value)}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="beklemede">Beklemede</SelectItem>
                                <SelectItem value="onaylandi">Onaylandı</SelectItem>
                                <SelectItem value="odendi">Ödendi</SelectItem>
                                <SelectItem value="iptal">İptal</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="mt-2 space-y-1">
                          <Label className="text-xs">Açıklama</Label>
                          <Input
                            value={hakedis.aciklama || ''}
                            onChange={(e) => updateEditHakedis(hakedis.id, 'aciklama', e.target.value)}
                            placeholder="Hakediş açıklaması..."
                            className="h-9"
                          />
                        </div>

                        {/* Hakediş Belgeleri */}
                        <div className="mt-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Label className="text-xs">Belgeler</Label>
                            <input
                              type="file"
                              multiple
                              onChange={(e) => handleEditHakedisFileUpload(e, hakedis.id)}
                              className="hidden"
                              id={`hakedis-file-${hakedis.id}`}
                              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => document.getElementById(`hakedis-file-${hakedis.id}`).click()}
                              className="h-6 text-xs gap-1"
                            >
                              <Upload className="h-3 w-3" />
                              Ekle
                            </Button>
                          </div>
                          {hakedis.belgeler?.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {hakedis.belgeler.map((doc, docIndex) => (
                                <div key={docIndex} className="flex items-center gap-1 bg-green-100 rounded px-2 py-1 text-xs">
                                  <File className="h-3 w-3 text-green-600" />
                                  <span className="max-w-[100px] truncate">{doc.filename}</span>
                                  <button
                                    type="button"
                                    onClick={() => removeEditHakedisDocument(hakedis.id, docIndex)}
                                    className="text-red-500 hover:text-red-700 ml-1"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Toplam Hakediş Tutarı */}
                    <div className="bg-green-100 rounded-lg p-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-5 w-5 text-green-700" />
                        <span className="font-medium text-green-800">Toplam Hakediş:</span>
                      </div>
                      <span className="text-lg font-bold text-green-700">
                        {getEditTotalHakedis().toLocaleString('tr-TR')} {currencySymbols[selectedService?.currency] || '₺'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            {isEditing ? (
              <>
                <Button variant="outline" onClick={cancelEditing} disabled={editLoading}>
                  İptal
                </Button>
                <Button 
                  onClick={handleSaveEdit} 
                  className="bg-green-600 hover:bg-green-700 gap-2"
                  disabled={editLoading}
                >
                  {editLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Kaydet
                </Button>
              </>
            ) : (
              <div className="flex gap-2 w-full justify-between">
                <Button variant="outline" onClick={() => setShowViewModal(false)}>
                  Kapat
                </Button>
                {selectedService && selectedService.status === 'aktif' && (
                  <Button 
                    onClick={openCompleteModal}
                    className="bg-blue-600 hover:bg-blue-700 gap-2"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Hizmet Alımını Sonlandır
                  </Button>
                )}
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Görevi Tamamla Modal */}
      <Dialog open={showCompleteModal} onOpenChange={setShowCompleteModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-blue-600" />
              Hizmet Alımını Sonlandır
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedService && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-500">Hizmet</p>
                <p className="font-semibold">{selectedService.service_name}</p>
                <p className="text-sm text-gray-500 mt-1">Hizmet No: {selectedService.service_no}</p>
              </div>
            )}

            <div>
              <Label htmlFor="complete-description">Açıklama *</Label>
              <Textarea
                id="complete-description"
                placeholder="Tamamlama açıklaması yazın..."
                value={completeData.description}
                onChange={(e) => setCompleteData(prev => ({ ...prev, description: e.target.value }))}
                className="mt-1"
                rows={4}
              />
            </div>

            <div>
              <Label>Belgeler (Opsiyonel)</Label>
              <div className="mt-2">
                <input
                  type="file"
                  ref={completeFileInputRef}
                  onChange={handleCompleteFileUpload}
                  multiple
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => completeFileInputRef.current?.click()}
                  className="gap-2"
                >
                  <Upload className="h-4 w-4" />
                  Belge Ekle
                </Button>
              </div>

              {completeData.documents.length > 0 && (
                <div className="mt-3 space-y-2">
                  {completeData.documents.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded-lg">
                      <div className="flex items-center gap-2">
                        <File className="h-4 w-4 text-blue-600" />
                        <a 
                          href={doc.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline truncate max-w-[200px]"
                        >
                          {doc.name}
                        </a>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeCompleteDocument(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompleteModal(false)} disabled={completeLoading}>
              İptal
            </Button>
            <Button 
              onClick={handleCompleteService}
              className="bg-blue-600 hover:bg-blue-700 gap-2"
              disabled={completeLoading}
            >
              {completeLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
              Tamamla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchasingServices;
